import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { Enum_Historyevent_Showdateas, HistoryEvent } from "@/app/lib/generated/graphql-types";
import { mediaRefUrl } from "@/app/features/shared/__to_be_integrated/types/general/media/media-helper";
import VisiblyScrollableV2 from "@/app/features/shared/__to_be_integrated/ui/v2/VisiblyScrollable";
import styles from "./HistoryEventView.module.scss";
import HistoryEventImageModal from "@/app/features/sites/history/ui/public/HistoryEventImageModal.client";
import HistoryEventPdfLink from "@/app/features/sites/history/ui/shared/HistoryEventPdfLink";
import {
    HistoryEventDateFrame,
    HistoryEventViewFrame,
} from "@/app/features/sites/history/ui/shared/HistoryEventFrames";

interface HistoryEventViewProps {
    event: HistoryEvent;
    headingId: string;
    animated?: boolean;
}

const monthFormatter = new Intl.DateTimeFormat("de-DE", { month: "short" });
const yearFormatter = new Intl.DateTimeFormat("de-DE", { year: "numeric" });
const dayMonthFormatter = new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
});

const markdownSanitizeSchema = {
    ...defaultSchema,
    tagNames: [...(defaultSchema.tagNames ?? []), "span"],
    attributes: {
        ...(defaultSchema.attributes ?? {}),
        span: [["className", "md-text-small", "md-text-normal", "md-text-large", "md-text-xlarge"]],
    },
};

export default function HistoryEventView({ event, headingId, animated = true }: HistoryEventViewProps) {
    const startDate = parseHistoryDate(event.startDate);
    const endDate = event.endDate ? parseHistoryDate(event.endDate) : null;
    const dateLabel = formatHistoryDateLabel(startDate, event.showDateAs);
    const startYear = yearFormatter.format(startDate);
    const endYear = endDate ? yearFormatter.format(endDate) : null;
    const image = event.image ?? null;
    const readMoreUrl = event.readMore ? mediaRefUrl(event.readMore) : null;

    return (
        <HistoryEventViewFrame
            animated={animated}
            image={image ? <HistoryEventImageModal image={image} eventTitle={event.title} /> : undefined}
            date={<HistoryEventDateFrame dateLabel={dateLabel} startYear={startYear} endYear={endYear} />}
            title={<h2 id={headingId}>{event.title}</h2>}
            description={(
                <VisiblyScrollableV2
                    className={styles.descriptionScrollable}
                    scrollContainerAriaLabel={`Beschreibung zu ${event.title}`}
                >
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkBreaks]}
                        rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeSchema]]}
                    >
                        {event.description}
                    </ReactMarkdown>
                </VisiblyScrollableV2>
            )}
            actions={readMoreUrl ? (
                <HistoryEventPdfLink eventTitle={event.title} url={readMoreUrl} />
            ) : undefined}
        />
    );
}

function parseHistoryDate(value: string): Date {
    return new Date(`${value}T00:00:00`);
}

function formatHistoryDateLabel(date: Date, showDateAs: Enum_Historyevent_Showdateas): string {
    switch (showDateAs) {
        case Enum_Historyevent_Showdateas.DayAndYear:
            return dayMonthFormatter.format(date);
        case Enum_Historyevent_Showdateas.MonthAndYear:
            return monthFormatter.format(date);
        case Enum_Historyevent_Showdateas.YearOnly:
        default:
            return "Anno";
    }
}
